import { cn } from '@ifixit/ui/lib/utils';
import type { ComponentProps, ReactNode } from 'react';

/**
 * Exported for code that has to bolt the card look onto DOM it doesn't
 * render (relocated legacy frame modules); keep these literals so Tailwind's
 * scanner sees the classes.
 */
export const SECTION_CARD_CLASSES =
   '!rounded-2xl border border-gray-300 my-0 !bg-white !px-6 !py-8 text-sm [&_input]:max-w-3xl [&_button]:max-w-3xl max-md:!rounded-none max-md:border-x-0 max-md:border-b-0 max-md:!px-4 max-md:!py-4';
/** Mirrors SECTION_HEADING_CLASS (Shared/editor/field); kept literal so
 *  Tailwind's scanner sees it. */
export const SECTION_CARD_TITLE_CLASSES = '!pt-0 mb-3 text-sm font-semibold text-gray-950';

/**
 * The side gutter a non-card row restores below md, where the root drops its
 * horizontal padding to let SectionCard go full-bleed. `w-auto` is the
 * load-bearing half: `[data-slot="alert"]` is `w-full`, and a percentage width
 * stays pinned to the containing block, so a lone `mx-4` lands the margins
 * *outside* the box — displacing the alert 16px right and adding horizontal
 * page scroll rather than insetting it.
 */
export const EDGE_INSET_CLASSES = 'max-md:mx-4 max-md:w-auto';

/**
 * A white card wrapping one editor section. Border and radius match the
 * guide-editor Figma (rounded-2xl, 1px gray-300 border); the title reads at
 * the same weight as the section headings inside a card, so a titled card
 * and a section within one don't disagree. Below md the card goes full-bleed: square
 * corners, no side borders, and 16px side padding — callers drop their own
 * horizontal padding below md to match. It also drops its bottom border
 * there, leaving only the top one, so two stacked cards show a single
 * hairline between them instead of doubling their adjacent borders. Shared
 * by the wiki and guide editors so design tweaks land in both.
 */
export function SectionCard({
   title,
   className,
   children,
   ...props
}: ComponentProps<'section'> & { title?: string; children: ReactNode }) {
   return (
      <section className={cn(SECTION_CARD_CLASSES, className)} {...props}>
         {title && <h2 className={SECTION_CARD_TITLE_CLASSES}>{title}</h2>}
         {children}
      </section>
   );
}
